import { pickedLevers, type Goal } from "@valuemap/core";
import type { ReactNode } from "react";
import styles from "./Edit.module.css";

/**
 * "Say it back to the room" (reference `updateReadback`). The reference builds HTML strings; this
 * builds the same sentences as components, so nothing is ever injected as markup (brief 11).
 */

const lc = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

function Beat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.rbBeat}>
      <span className={styles.rbLabel}>{label}</span>
      <span className={styles.rbBody}>{children}</span>
    </div>
  );
}

function Highlight({ children }: { children: ReactNode }) {
  return <span className={styles.hl}>{children}</span>;
}

export function Readback({ goal, orgName }: { goal: Goal; orgName: string }) {
  const org = orgName.trim() || "this organisation";
  const beats: ReactNode[] = [];

  if (goal.rightNow.trim()) {
    beats.push(
      <Beat key="now" label="Right now">
        {goal.rightNow.trim()}
      </Beat>,
    );
  }

  const capability = goal.capability.trim();
  const objective = goal.objective.trim();
  let whatIf: ReactNode = null;
  if (capability) {
    whatIf = (
      <>
        <Highlight>{org}</Highlight> could {lc(capability)}
        {objective ? <>, so you {lc(objective)}</> : null}
      </>
    );
  } else if (objective) {
    whatIf = <>you could {lc(objective)}</>;
  }
  if (whatIf) {
    beats.push(
      <Beat key="whatif" label="What if">
        {whatIf}
      </Beat>,
    );
  }

  const outcome = goal.outcome.trim();
  const picked = pickedLevers(goal);
  const measures = picked.filter((p) => p.measure.trim());
  let soThat: ReactNode = outcome || null;
  if (picked.length) {
    const lead = outcome ? "the value shows up in " : "The value shows up in ";
    const value = (
      <>
        {lead}
        <Highlight>{picked.map((p) => lc(p.name)).join(", ")}</Highlight>
        {measures.length ? (
          <>, measured by {measures.map((p) => p.measure.trim()).join(", ")}</>
        ) : null}
      </>
    );
    soThat = outcome ? (
      <>
        {outcome}, and {value}
      </>
    ) : (
      value
    );
  }
  if (soThat) {
    beats.push(
      <Beat key="sothat" label="So that">
        {soThat}
      </Beat>,
    );
  }

  const cost = goal.costInaction.trim();
  return (
    <div className={styles.readback} aria-label="Read-back">
      <div className={`eyebrow ${styles.eyebrow}`}>Say it back to the room</div>
      <div className={styles.rbBeats}>
        {beats.length === 0 ? (
          <div className={styles.rbEmpty}>
            Capture the goal above and the value story builds here for you to read back.
          </div>
        ) : (
          <>
            {beats}
            {cost ? (
              <div className={styles.rbCost}>
                <span>The cost of staying as we are:</span> {cost}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
